import api from './api.service';

export type SalesOrderTimesheetStatus =
  | 'Draft'
  | 'Submitted'
  | 'Approved'
  | 'Rejected'
  | 'Invoiced';

export interface SalesOrderTimesheetDay {
  id: string;
  person_id: string;
  work_date: string;
  attendance_code: string;
}

export interface SalesOrderTimesheetPerson {
  id: string;
  timesheet_id: string;
  line_number: number;
  person_name: string;
  position: string;
  badge_id?: string | null;
  remarks?: string | null;
  sales_order_item_id?: string | null;
  days?: SalesOrderTimesheetDay[];
}

export interface SalesOrderTimesheet {
  id: string;
  company_id: string;
  sales_order_id: string;
  period_year: number;
  period_month: number;
  status: SalesOrderTimesheetStatus;
  department_id?: string | null;
  sales_invoice_id?: string | null;
  approval_instance_id?: string | null;
  approval_status?: string;
  notes?: string | null;
  people?: SalesOrderTimesheetPerson[];
  created_at?: string;
  updated_at?: string;
}

export interface TimesheetBillingPreviewLine {
  sales_order_item_id: string;
  line_number: number;
  item_description: string;
  ordered_quantity: number;
  unit_price: number;
  uom: string | null;
  attendance_exists: boolean;
  people_count: number;
  avg_billable_days: number;
  calendar_days: number;
  prorata: number;
  proposed_quantity: number;
  proposed_unit_price: number;
  proposed_line_amount: number;
  unmatched_people: number;
}

export interface TimesheetBillingPreview {
  timesheet_id: string;
  period_year: number;
  period_month: number;
  calendar_days: number;
  lines: TimesheetBillingPreviewLine[];
  billable_line_count: number;
}

function unwrap<T>(response: unknown): T {
  const res = response as { data?: T | { data?: T }; success?: boolean };
  if (res?.data && typeof res.data === 'object' && 'data' in (res.data as object)) {
    return (res.data as { data: T }).data;
  }
  if (res?.data !== undefined) return res.data as T;
  return response as T;
}

class SalesOrderTimesheetService {
  async listAll(filters?: {
    search?: string;
    status?: string;
    sales_order_id?: string;
    customer_id?: string;
  }): Promise<
    Array<{
      id: string;
      sales_order_id: string;
      sales_order_number: string;
      customer_id: string;
      customer_name: string | null;
      period_year: number;
      period_month: number;
      status: string;
      approval_status: string;
      sales_invoice_id: string | null;
      created_at?: string;
    }>
  > {
    const params = new URLSearchParams();
    if (filters?.search) params.set('search', filters.search);
    if (filters?.status) params.set('status', filters.status);
    if (filters?.sales_order_id) params.set('sales_order_id', filters.sales_order_id);
    if (filters?.customer_id) params.set('customer_id', filters.customer_id);
    const qs = params.toString();
    const response = await api.get(`/sales/order-timesheets${qs ? `?${qs}` : ''}`);
    const data = unwrap<any>(response);
    return Array.isArray(data) ? data : data?.data ?? [];
  }

  async listBySalesOrder(salesOrderId: string): Promise<SalesOrderTimesheet[]> {
    const response = await api.get(`/sales/orders/${salesOrderId}/timesheets`);
    const data = unwrap<SalesOrderTimesheet[] | { data?: SalesOrderTimesheet[] }>(response);
    return Array.isArray(data) ? data : (data as any)?.data ?? [];
  }

  async listApprovedForInvoice(): Promise<
    Array<{
      id: string;
      sales_order_id: string;
      sales_order_number: string;
      period_year: number;
      period_month: number;
      customer_name: string | null;
    }>
  > {
    const response = await api.get('/sales/order-timesheets/approved-for-invoice');
    const data = unwrap<any>(response);
    return Array.isArray(data) ? data : data?.data ?? [];
  }

  async getById(id: string): Promise<SalesOrderTimesheet> {
    const response = await api.get(`/sales/order-timesheets/${id}`);
    return unwrap<SalesOrderTimesheet>(response);
  }

  async create(
    salesOrderId: string,
    body: {
      period_year: number;
      period_month: number;
      copy_people_from_prior_month?: boolean;
      notes?: string;
    }
  ): Promise<SalesOrderTimesheet> {
    const response = await api.post(`/sales/orders/${salesOrderId}/timesheets`, body);
    return unwrap<SalesOrderTimesheet>(response);
  }

  async importExcel(
    salesOrderId: string,
    file: File,
    opts?: { period_year?: number; period_month?: number }
  ): Promise<SalesOrderTimesheet> {
    const form = new FormData();
    form.append('file', file);
    if (opts?.period_year) form.append('period_year', String(opts.period_year));
    if (opts?.period_month) form.append('period_month', String(opts.period_month));
    const response = await api.post(`/sales/orders/${salesOrderId}/timesheets/import`, form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return unwrap<SalesOrderTimesheet>(response);
  }

  async downloadImportTemplate(opts?: {
    period_year?: number;
    period_month?: number;
  }): Promise<void> {
    const params = new URLSearchParams();
    if (opts?.period_year) params.set('period_year', String(opts.period_year));
    if (opts?.period_month) params.set('period_month', String(opts.period_month));
    const qs = params.toString();
    const y = opts?.period_year || new Date().getFullYear();
    const m = opts?.period_month || new Date().getMonth() + 1;
    const res = await api.getClient().get(`/sales/order-timesheets/import-template${qs ? `?${qs}` : ''}`, {
      responseType: 'blob',
    });
    const blob = new Blob([res.data], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `sales-order-timesheet-import-template-${y}-${String(m).padStart(2, '0')}.xlsx`;
    a.click();
    window.URL.revokeObjectURL(url);
  }

  async addPerson(
    timesheetId: string,
    body: {
      person_name: string;
      position: string;
      badge_id?: string | null;
      remarks?: string | null;
      sales_order_item_id?: string | null;
    }
  ): Promise<SalesOrderTimesheet> {
    const response = await api.post(`/sales/order-timesheets/${timesheetId}/people`, body);
    return unwrap<SalesOrderTimesheet>(response);
  }

  async removePerson(timesheetId: string, personId: string): Promise<SalesOrderTimesheet> {
    const response = await api.delete(`/sales/order-timesheets/${timesheetId}/people/${personId}`);
    return unwrap<SalesOrderTimesheet>(response);
  }

  async updateDay(
    timesheetId: string,
    personId: string,
    work_date: string,
    attendance_code: string
  ): Promise<SalesOrderTimesheet> {
    const response = await api.patch(
      `/sales/order-timesheets/${timesheetId}/people/${personId}/days`,
      { work_date, attendance_code }
    );
    return unwrap<SalesOrderTimesheet>(response);
  }

  async savePersonDays(
    timesheetId: string,
    personId: string,
    days: Array<{ work_date: string; attendance_code?: string | null }>,
    options?: { replace_all?: boolean }
  ): Promise<SalesOrderTimesheet> {
    const response = await api.put(
      `/sales/order-timesheets/${timesheetId}/people/${personId}/days`,
      {
        days,
        replace_all: options?.replace_all !== false,
      }
    );
    return unwrap<SalesOrderTimesheet>(response);
  }

  async billingPreview(id: string): Promise<TimesheetBillingPreview> {
    const response = await api.get(`/sales/order-timesheets/${id}/billing-preview`);
    return unwrap<TimesheetBillingPreview>(response);
  }

  async resolveMatches(id: string): Promise<SalesOrderTimesheet> {
    const response = await api.post(`/sales/order-timesheets/${id}/resolve-matches`);
    return unwrap<SalesOrderTimesheet>(response);
  }

  async mapPerson(
    timesheetId: string,
    personId: string,
    sales_order_item_id: string | null
  ): Promise<SalesOrderTimesheet> {
    const response = await api.patch(`/sales/order-timesheets/${timesheetId}/people/${personId}`, {
      sales_order_item_id,
    });
    return unwrap<SalesOrderTimesheet>(response);
  }

  async submit(id: string): Promise<SalesOrderTimesheet> {
    const response = await api.post(`/sales/order-timesheets/${id}/submit`);
    return unwrap<SalesOrderTimesheet>(response);
  }

  async approve(id: string, remarks?: string): Promise<SalesOrderTimesheet> {
    const response = await api.post(`/sales/order-timesheets/${id}/approve`, { remarks });
    return unwrap<SalesOrderTimesheet>(response);
  }

  async reject(id: string, payload: { rejection_reason_code?: string; remarks?: string }): Promise<SalesOrderTimesheet> {
    const response = await api.post(`/sales/order-timesheets/${id}/reject`, {
      rejection_reason_code: payload.rejection_reason_code || 'other',
      remarks: payload.remarks || 'Rejected',
    });
    return unwrap<SalesOrderTimesheet>(response);
  }

  async revokeApproval(id: string, reason: string): Promise<SalesOrderTimesheet> {
    const response = await api.post(`/sales/order-timesheets/${id}/revoke-approval`, { reason });
    return unwrap<SalesOrderTimesheet>(response);
  }

  async createInvoice(id: string): Promise<unknown> {
    const response = await api.post(`/sales/order-timesheets/${id}/create-invoice`);
    return unwrap(response);
  }
}

const salesOrderTimesheetService = new SalesOrderTimesheetService();
export default salesOrderTimesheetService;
